/* Hallmark · macrostructure: Single Column Report (app) · tone: technical · theme: console-system · genre: modern-minimal */
import {
  type BillingInterval,
  type SubscriptionApiClient,
  useSubscription,
} from '@xhubio-saas/core-frontend/plugin-subscription'
import { type UsageApiClient, useUsageAggregation } from '@xhubio-saas/core-frontend/plugin-usage'
import { Button, Skeleton } from '@xhubio-saas/core-frontend/ui-components'
import { CircleAlert, ExternalLink, LoaderCircle } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { BillingHeader } from './billing/BillingHeader'
import { BillingTabs } from './billing/BillingTabs'
import {
  BILLING_TAB_STORAGE_KEY,
  type BillingTab,
  billingStateOf,
  isBillingTab,
  isFreeSubscription,
  stateDateOf,
} from './billing/billingState'
import { type BuyerCountryInput, CountryDialog } from './billing/CountryDialog'
import { InvoicesSection } from './billing/InvoicesSection'
import { PREVIEW_INVOICES } from './billing/invoicesPreview'
import { PlansSection } from './billing/PlansSection'
import { QuotaMeters } from './billing/QuotaMeters'
import { UsageSection } from './billing/UsageSection'

/**
 * Billing — plan, status, quota, plans and usage in one place (plan API 2026-10-11/02).
 *
 * Before: „Settings → Subscription" (core `SubscriptionPage`) and „Analytics → Usage" (core `UsagePage`), apart, plus
 * an overage card for overage that is not billed. Now one page in the console's report pattern: headline (plan and
 * status, like the validation verdict) → tabs Overview · Plans · Usage.
 *
 * 🔴 Only the calls the console clients already make (E2): `getSubscription`, `getPlan`, `getPublishedPlanDisplay`,
 * quota status, `getUsageHistory`, checkout, portal, downgrade. Invoices live in the provider's portal until billing
 * brings `listInvoices` (M2). Provider-neutral: the page never names Lemon Squeezy or Stripe.
 *
 * ⚪ App-local (E1): the building blocks come from core-frontend (`useSubscription`, `PricingCard`, `EnterpriseCard`,
 * `BillingIntervalSelector`, `useUsageAggregation`, `useUsageHistory`, `UsageChart`, `Progress`); nothing in the
 * package changes.
 */

/** Tab ids for which a role needs the right to change the plan (E7). */
const MANAGE_ONLY_TABS: readonly BillingTab[] = ['plans']

/** ⚪ ENTWURF (plan API 2026-10-11/03): the invoices tab shows sample rows on the dev server only, until billing brings `listInvoices`. */
const SHOW_INVOICES_PREVIEW = import.meta.env.DEV

function readStoredTab(): string | null {
  try {
    return sessionStorage.getItem(BILLING_TAB_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredTab(tab: BillingTab): void {
  try {
    sessionStorage.setItem(BILLING_TAB_STORAGE_KEY, tab)
  } catch {
    // Without storage the choice lives in the address only.
  }
}

export type { BuyerCountryInput }

/**
 * The organization's country before a paid checkout (plan API 2026-10-09/02). Billing prices and taxes by it, and
 * without it the checkout refuses with `billing-checkout-001` — so the page asks first (E1).
 */
export interface BuyerCountry {
  /** `organization.current.countryCode`; `null` = not set yet. */
  countryCode: string | null
  /** Role owner/admin — only they may set it (`organization.update`, E5). */
  canSet: boolean
  /** Sets the country once (`organization.update`); rejects on a refusal. */
  setCountry: (input: BuyerCountryInput) => Promise<void>
}

export interface BillingPageProps {
  subscriptionClient: SubscriptionApiClient
  usageClient: UsageApiClient
  /**
   * May this role change the plan — checkout, downgrade, portal (E7: `billing:subscription:read`, the gate of the
   * former `/subscription`). Without it the page shows plan, status, quota and usage, but no plan tab and no buttons.
   */
  canManagePlan: boolean
  /** `de-DE`, `en-US` … for numbers and dates. */
  locale: string
  /** Event type → display name for the usage breakdown. */
  eventTypeLabels: Record<string, string>
  /**
   * The organization's country. Absent (still loading): checkout as before — the server's `billing-checkout-001`
   * stays the fallback (E8).
   */
  buyerCountry?: BuyerCountry
}

export function BillingPage({
  subscriptionClient,
  usageClient,
  canManagePlan,
  locale,
  eventTypeLabels,
  buyerCountry,
}: BillingPageProps) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    subscription,
    plan,
    pricingTiers,
    pricingPlans,
    discounts,
    featureDisplayData,
    isLoading,
    error,
    lastUpdated,
    refresh,
    fetchPortalUrl,
  } = useSubscription(subscriptionClient)

  // While the subscription loads, `subscription` is null as well — the usage query waits until it has loaded once, else
  // it goes out first with the calendar-month fallback below (issue 2026-10-13). A refresh keeps the shown period.
  const subscriptionSettled = !isLoading || lastUpdated !== null

  // The current period — from the subscription, else the calendar month (free plan without a subscription row).
  const period = useMemo(() => {
    if (subscription) {
      return { startDate: subscription.currentPeriodStart, endDate: subscription.currentPeriodEnd }
    }
    const now = new Date()
    return {
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
    }
  }, [subscription])
  const {
    aggregation,
    isLoading: quotaLoading,
    refetch: refetchQuota,
  } = useUsageAggregation(usageClient, period)

  // ── Tabs: `?tab=` first (old addresses land here), then the tab of this session, then the overview ──────────────
  const allowed = (tab: string | null): tab is BillingTab =>
    isBillingTab(tab) &&
    (tab !== 'invoices' || SHOW_INVOICES_PREVIEW) &&
    (canManagePlan || !MANAGE_ONLY_TABS.includes(tab))
  const fromUrl = searchParams.get('tab')
  const [stored, setStored] = useState<string | null>(() => readStoredTab())
  const activeTab: BillingTab = allowed(fromUrl) ? fromUrl : allowed(stored) ? stored : 'overview'
  const selectTab = useCallback(
    (tab: BillingTab) => {
      setStored(tab)
      writeStoredTab(tab)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('tab', tab)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // ── Return from checkout / portal (E8) ───────────────────────────────────────────────────────────────────────────
  // The clients invalidate `getSubscription` and the quota on every read, so the mount after the checkout's return
  // URL is fresh by itself. The portal opens in a new tab — when the console tab becomes visible again, read both
  // once more.
  const awaitingReturn = useRef(false)
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible' || !awaitingReturn.current) return
      awaitingReturn.current = false
      void refresh()
      void refetchQuota()
    }
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    return () => {
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
    }
  }, [refresh, refetchQuota])

  // ── Portal (verbatim behaviour of core SubscriptionPage) ─────────────────────────────────────────────────────────
  const [portalLoading, setPortalLoading] = useState(false)
  const openPortal = useCallback(async () => {
    setPortalLoading(true)
    try {
      const url = await fetchPortalUrl()
      if (url) {
        awaitingReturn.current = true
        // Popup blocked after the await: continue in the same tab.
        if (!window.open(url, '_blank')) window.location.assign(url)
      }
    } catch {
      // Caught, not swallowed: the provider's refusal is spoken by the kernel's global error toast
      // (MutationCache.onError). Here only the button is released (finally).
    } finally {
      setPortalLoading(false)
    }
  }, [fetchPortalUrl])

  // ── Checkout / downgrade (verbatim behaviour of core SubscriptionPage) ───────────────────────────────────────────
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null)
  const startCheckout = useCallback(
    async (planId: string, interval: BillingInterval) => {
      if (!subscriptionClient.createCheckoutSession) return
      setCheckoutPlanId(planId)
      try {
        const result = await subscriptionClient.createCheckoutSession({
          planId,
          billingPeriod: interval,
        })
        window.location.href = result.checkoutUrl
      } catch (err) {
        console.error('[Billing] checkout failed:', err)
        setCheckoutPlanId(null)
      }
    },
    [subscriptionClient]
  )

  // ── Country before checkout (plan API 2026-10-09/02) ─────────────────────────────────────────────────────────────
  // A paid plan without the organization's country opens the dialog; the chosen plan waits until it is set.
  const [awaitingCountry, setAwaitingCountry] = useState<{
    planId: string
    interval: BillingInterval
  } | null>(null)
  const confirmCountry = useCallback(
    async (input: BuyerCountryInput) => {
      if (!buyerCountry || !awaitingCountry) return
      await buyerCountry.setCountry(input)
      setAwaitingCountry(null)
      await startCheckout(awaitingCountry.planId, awaitingCountry.interval)
    },
    [buyerCountry, awaitingCountry, startCheckout]
  )

  const selectPlan = useCallback(
    async (planId: string, interval: BillingInterval) => {
      if (planId === 'free') {
        if (!subscriptionClient.downgradeToFree) return
        setCheckoutPlanId(planId)
        try {
          await subscriptionClient.downgradeToFree()
          await Promise.all([refresh(), refetchQuota()])
        } catch (err) {
          // The refusal is spoken by the global error toast — a banner here would be a second speaker.
          console.error('[Billing] downgrade to free failed:', err)
        } finally {
          setCheckoutPlanId(null)
        }
        return
      }
      if (buyerCountry && !buyerCountry.countryCode) {
        setAwaitingCountry({ planId, interval })
        return
      }
      await startCheckout(planId, interval)
    },
    [subscriptionClient, refresh, refetchQuota, buyerCountry, startCheckout]
  )

  // Chart legend: the translated event type name, else the quota type's catalog name (E4) — never the raw
  // `quota:api_calls` id (seen in the browser 2026-10-11: the `usage` bundle has no entry for quota ids).
  const usageLabels = useMemo(() => {
    const fromCatalog = Object.fromEntries(
      (aggregation?.byHierarchy ?? []).map((node) => [node.pattern, node.label])
    )
    return { ...fromCatalog, ...eventTypeLabels }
  }, [aggregation, eventTypeLabels])

  const state = billingStateOf(subscription)
  const isPaid = !isFreeSubscription(subscription)
  const canOpenPortal = canManagePlan && isPaid

  const title = (
    <div>
      <h1 className="min-w-0 text-3xl tracking-tight [overflow-wrap:anywhere]">
        {t('billingPage.title')}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t('billingPage.subtitle')}</p>
    </div>
  )

  if (error && !lastUpdated) {
    return (
      <div className="container mx-auto space-y-6 px-4 py-6" data-testid="billing-page">
        {title}
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
          role="alert"
          data-testid="billing-error"
        >
          <CircleAlert className="h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm">{t('billingPage.error')}</p>
          <Button variant="outline" className="whitespace-nowrap" onClick={() => void refresh()}>
            {t('billingPage.retry')}
          </Button>
        </div>
      </div>
    )
  }

  const changePlanButton = canManagePlan ? (
    <Button
      variant={isPaid ? 'outline' : 'default'}
      className="whitespace-nowrap"
      onClick={() => selectTab('plans')}
      data-testid="billing-btn-change-plan"
    >
      {t('billingPage.actions.changePlan')}
    </Button>
  ) : null
  const portalButton = canOpenPortal ? (
    <Button
      variant={state === 'pastDue' ? 'default' : 'outline'}
      className="whitespace-nowrap"
      onClick={() => void openPortal()}
      disabled={portalLoading}
      data-testid="billing-btn-portal"
    >
      {portalLoading ? (
        <LoaderCircle
          className="h-4 w-4 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : (
        <ExternalLink className="h-4 w-4" aria-hidden="true" />
      )}
      {t('billingPage.actions.managePayment')}
    </Button>
  ) : null

  const panels = [
    {
      id: 'overview' as const,
      label: t('billingPage.tab.overview'),
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{t('billingPage.quota.title')}</h3>
          <QuotaMeters
            nodes={aggregation?.byHierarchy ?? null}
            isLoading={quotaLoading}
            resetDate={period.endDate}
            locale={locale}
          />
          {canOpenPortal && (
            <p
              className="border-t pt-4 text-sm text-muted-foreground"
              data-testid="billing-portal-hint"
            >
              {t('billingPage.portalHint')}
            </p>
          )}
        </div>
      ),
    },
    ...(canManagePlan
      ? [
          {
            id: 'plans' as const,
            label: t('billingPage.tab.plans'),
            content: (
              <PlansSection
                tiers={pricingTiers}
                pricingPlans={pricingPlans}
                discounts={discounts}
                featureDisplayData={featureDisplayData}
                currentPlanId={subscription?.planId ?? null}
                currentPlanName={plan?.name ?? null}
                onSelect={(planId, interval) => void selectPlan(planId, interval)}
                loadingPlanId={checkoutPlanId}
              />
            ),
          },
        ]
      : []),
    {
      id: 'usage' as const,
      label: t('billingPage.tab.usage'),
      content: subscriptionSettled ? (
        <UsageSection
          client={usageClient}
          periodStart={period.startDate}
          periodEnd={period.endDate}
          locale={locale}
          eventTypeLabels={usageLabels}
        />
      ) : (
        <Skeleton className="h-64 w-full" data-testid="billing-usage-loading" />
      ),
    },
    ...(SHOW_INVOICES_PREVIEW
      ? [
          {
            id: 'invoices' as const,
            label: t('billingPage.tab.invoices'),
            content: (
              <InvoicesSection
                invoices={PREVIEW_INVOICES}
                hasMore
                isLoadingMore={false}
                onLoadMore={() => undefined}
                onDownload={(invoiceId, art) =>
                  console.info('[Billing] Entwurf: Download', invoiceId, art)
                }
                locale={locale}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <div className="container mx-auto space-y-6 px-4 py-6" data-testid="billing-page">
      {title}

      {/* 🔴 One column at every width, full page width, nothing sticky (pattern of validation/conversion). */}
      <div className="grid grid-cols-1 gap-6" data-testid="billing-layout">
        {isLoading && !lastUpdated ? (
          <div
            className="space-y-3 border-t-2 border-border pt-3"
            data-testid="billing-header-loading"
          >
            <Skeleton className="h-8 w-72 max-w-full" />
          </div>
        ) : (
          <BillingHeader
            state={state}
            planName={plan?.name}
            date={stateDateOf(state, subscription)}
            locale={locale}
            actions={
              changePlanButton || portalButton ? (
                <>
                  {changePlanButton}
                  {portalButton}
                </>
              ) : undefined
            }
          />
        )}

        <BillingTabs
          panels={panels}
          value={activeTab}
          onValueChange={selectTab}
          label={t('billingPage.title')}
        />
      </div>

      {awaitingCountry && buyerCountry && (
        <CountryDialog
          canSet={buyerCountry.canSet}
          onCancel={() => setAwaitingCountry(null)}
          onConfirm={confirmCountry}
        />
      )}
    </div>
  )
}
